<script setup lang="ts">
/**
 * Structured view of an order's lab results payload. Mirrors the legacy
 * `app/projects/data/results_modal.html` — different shape than
 * `OrderDetailsDialog` (the raw survey data):
 *
 *   results.reservationCode, results.confirmedAt           // camelCase top level
 *   results.lab.{name, address, city, state, zip}
 *   results.user.{name, company.{name, city, state, zip}}
 *   results.details.samples[]              // each sample has analysisResults
 *   results.details.materials[]
 *   results.details.materialLocations[]    // camelCase
 *
 * Mounted via `useAppDialog().open(LabResultsDialog, { data: results })`.
 */
import { computed, inject, type Ref } from 'vue';
import { dedupeWords, formatCollectedDateTime, naturalCompare } from '@/utils/filters';

interface DialogRefValue {
  close: (data?: unknown) => void;
  data?: unknown;
}

interface Lab {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
}
interface Company {
  name?: string;
  city?: string;
  state?: string;
  zip?: string;
}
interface User {
  name?: string;
  company?: Company;
}
interface Photo {
  url: string;
}
type Sample = Record<string, unknown> & {
  analysisResults?: Array<{ results?: Array<Record<string, unknown>> }>;
};
interface Material {
  haGroup?: string;
  material?: string;
  color?: string;
  details?: string;
  photo?: Photo[];
}
interface MaterialLocation {
  haGroup?: string;
  location?: string;
  quantity?: number | string;
  units?: string;
  condition?: string;
}
interface ResultsDetails {
  samples?: Sample[];
  materials?: Material[];
  materialLocations?: MaterialLocation[];
}
interface LabResults {
  reservationCode?: string;
  confirmedAt?: string;
  lab?: Lab;
  user?: User;
  details?: ResultsDetails;
}

const dialogRef = inject<Ref<DialogRefValue | undefined>>('dialogRef');
const data = computed<LabResults>(
  () => (dialogRef?.value?.data as LabResults | undefined) ?? {},
);

const PREFERRED_HEADERS = [
  'customer_sample_id',
  'ha_group',
  'category',
  'matrix',
  'test_method',
  'turnaround_time',
];

function snakeToHuman(str: string): string {
  return str
    .split('_')
    .map((w) => (w.length ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

function getHeaders(samples: Sample[] | undefined): string[] {
  if (!samples) return [];
  const seen: string[] = [];
  for (const row of samples) {
    for (const key of Object.keys(row)) {
      if (!key.startsWith('$$') && key !== 'analysisResults' && !seen.includes(key)) {
        seen.push(key);
      }
    }
  }
  const front = PREFERRED_HEADERS.filter((h) => seen.includes(h));
  const rest = seen.filter((h) => !front.includes(h));
  return [...front, ...rest];
}

function isPhotoArray(v: unknown): v is Photo[] {
  return Array.isArray(v) && v.every((p) => typeof p === 'object' && p !== null && 'url' in p);
}

/** Same cell-formatter rule as the order modal — scrub `test_method` /
 *  `matrix` strings of duplicate category-prefix words, and show the collected
 *  timestamp in a friendly form without shifting it into the viewer's timezone. */
function formatCell(header: string, value: unknown): unknown {
  if (header === 'test_method' || header === 'matrix') return dedupeWords(value);
  if (header === 'datetime_collected' || header === 'datetimeCollected') {
    return formatCollectedDateTime(value);
  }
  return value ?? '';
}

/** Lab Results return samples as a flat array (no per-category grouping —
 *  that's the order modal's shape). Natural-sort by `customer_sample_id`,
 *  falling back to `[ha_group, customer_sample_id]` when any row has an
 *  `ha_group` (asbestos-bulk pattern). */
const samples = computed<Sample[]>(() => {
  const raw = data.value.details?.samples ?? [];
  if (raw.length < 2) return raw;
  const hasHaGroup = raw.some((s) => {
    const g = s.ha_group;
    return g != null && String(g).length > 0;
  });
  return [...raw].sort((a, b) => {
    if (hasHaGroup) {
      const c = naturalCompare(a.ha_group, b.ha_group);
      if (c !== 0) return c;
    }
    return naturalCompare(a.customer_sample_id, b.customer_sample_id);
  });
});
const hasResults = computed(() => samples.value.some((s) => (s.analysisResults?.length ?? 0) > 0));
</script>

<template>
  <div class="results">
    <!-- Header summary -->
    <section v-if="data.reservationCode || data.confirmedAt">
      <table class="results__kv">
        <tbody>
          <tr>
            <td>Reservation Code</td>
            <td class="results__mono">{{ data.reservationCode ?? '—' }}</td>
          </tr>
          <tr>
            <td>Confirmed At</td>
            <td>{{ data.confirmedAt ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <div class="results__split">
      <section v-if="data.lab" class="results__split-half">
        <h2>Lab Information</h2>
        <table class="results__kv">
          <tbody>
            <tr>
              <td>Lab</td>
              <td>{{ data.lab.name ?? '—' }}</td>
            </tr>
            <tr>
              <td>Address</td>
              <td>
                {{ data.lab.address ?? '—' }}<br />
                {{ data.lab.city }}, {{ data.lab.state }}<br />
                {{ data.lab.zip ?? '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <section v-if="data.user" class="results__split-half">
        <h2>Inspector Information</h2>
        <table class="results__kv">
          <tbody>
            <tr>
              <td>Inspector</td>
              <td>{{ data.user.name ?? '—' }}</td>
            </tr>
            <tr v-if="data.user.company">
              <td>Company</td>
              <td>
                {{ data.user.company.name ?? '—' }}<br />
                {{ data.user.company.city }}, {{ data.user.company.state }}<br />
                {{ data.user.company.zip ?? '' }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <section v-if="samples.length > 0">
      <h2>Samples</h2>
      <div v-for="(sample, i) in samples" :key="i" class="results__sample">
        <div class="results__scroll">
          <table class="results__table">
            <thead>
              <tr>
                <th v-for="header in getHeaders(samples)" :key="header">
                  {{ snakeToHuman(header) }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td v-for="header in getHeaders(samples)" :key="header">
                  <template v-if="header === 'photo' && isPhotoArray(sample[header])">
                    <img
                      v-for="(p, pi) in sample[header] as Photo[]"
                      :key="pi"
                      :src="p.url"
                      class="results__photo"
                      alt=""
                    />
                  </template>
                  <template v-else>{{ formatCell(header, sample[header]) }}</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="(sample.analysisResults?.length ?? 0) > 0" class="results__analysis">
          <h3>Results</h3>
          <div
            v-for="(res, ri) in sample.analysisResults?.[0]?.results ?? []"
            :key="ri"
            class="results__scroll results__scroll--inner"
          >
            <table class="results__analysis-table">
              <thead>
                <tr>
                  <th v-for="(_, k) in res" :key="String(k)">{{ snakeToHuman(String(k)) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td v-for="(v, k) in res" :key="String(k)">{{ v }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <p v-if="!hasResults" class="results__empty-results">
        Lab results have not been posted for these samples yet.
      </p>
    </section>

    <section v-if="(data.details?.materials?.length ?? 0) > 0">
      <h2>Materials</h2>
      <div class="results__scroll">
        <table class="results__table">
          <thead>
            <tr>
              <th>HA Group</th>
              <th>Material</th>
              <th>Color</th>
              <th>Details</th>
              <th>Photo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(m, i) in data.details?.materials ?? []" :key="i">
              <td>{{ m.haGroup ?? '—' }}</td>
              <td>{{ m.material ?? '—' }}</td>
              <td>{{ m.color ?? '—' }}</td>
              <td>{{ m.details ?? '—' }}</td>
              <td>
                <img
                  v-for="(p, pi) in m.photo ?? []"
                  :key="pi"
                  :src="p.url"
                  class="results__photo"
                  alt=""
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="(data.details?.materialLocations?.length ?? 0) > 0">
      <h2>Material Locations</h2>
      <div class="results__scroll">
        <table class="results__table">
          <thead>
            <tr>
              <th>HA Group</th>
              <th>Location</th>
              <th>Qty</th>
              <th>Units</th>
              <th>Condition</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(ml, i) in data.details?.materialLocations ?? []" :key="i">
              <td>{{ ml.haGroup ?? '—' }}</td>
              <td>{{ ml.location ?? '—' }}</td>
              <td>{{ ml.quantity ?? '—' }}</td>
              <td>{{ ml.units ?? '—' }}</td>
              <td>{{ ml.condition ?? '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<style scoped>
.results {
  padding: 0.5rem 0.25rem;
  color: #1f2937;
}
.results section {
  margin-bottom: 1.5rem;
}
.results h2 {
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.25rem;
}
.results h3 {
  margin: 0.5rem 0;
  font-size: 1rem;
  /* Match h2 + table thead so sub-section headings don't read as accent text. */
  color: #374151;
}
.results__split {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
  gap: 1.5rem;
}
.results__kv,
.results__table,
.results__analysis-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.results__kv td {
  padding: 0.375rem 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}
.results__kv td:first-child {
  width: 35%;
  font-weight: 600;
  color: #4b5563;
  background: #f9fafb;
}
.results__mono {
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
}
.results__scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  max-width: 100%;
}
/* Inner scroll inside a sample card — drop the border so it doesn't double up
   with the card's own border, but keep the horizontal-scroll behavior. */
.results__scroll--inner {
  border: 0;
  background: #fff;
  margin-bottom: 0.5rem;
}
/* Tables inside scrollers should keep their natural column widths rather than
   squishing to fit — that's the whole point of the scroll. */
.results__scroll .results__table,
.results__scroll .results__analysis-table {
  width: auto;
  min-width: 100%;
}
.results__scroll .results__table th,
.results__scroll .results__table td,
.results__scroll .results__analysis-table th,
.results__scroll .results__analysis-table td {
  white-space: nowrap;
}
.results__sample {
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #fafbff;
}
.results__table th,
.results__table td {
  padding: 0.375rem 0.625rem;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
  vertical-align: top;
  font-size: 0.8125rem;
}
.results__table thead th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.results__analysis {
  margin-top: 0.75rem;
}
.results__analysis-table {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
}
.results__analysis-table th,
.results__analysis-table td {
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.75rem;
}
.results__analysis-table thead th {
  background: #eef2ff;
  color: #3730a3;
}
.results__empty-results {
  margin: 1rem 0;
  padding: 0.75rem;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 0.375rem;
  color: #92400e;
}
.results__photo {
  max-width: 250px;
  display: inline-block;
  margin: 0 0.5rem 0.5rem 0;
}
</style>
